import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import {
  ChangeRequest,
  ChangeRequestStatus,
  EntityType as ChangeRequestEntityType,
} from '../types/changeRequest';
import { changeRequestService } from '../services/changeRequests';
import { enumMatches, tryNormalizeEnumValue } from '../utils/enumNormalize';
import {
  formatChangeRequestFieldName,
  formatChangeRequestFieldValue,
} from '../utils/formatChangeField';
import { ATHENS_TIME_ZONE, formatAthensDate } from '../utils/athensTime';
import Modal from '../components/common/Modal';
import FormField from '../components/common/FormField';
import EmptyState from '../components/common/EmptyState';
import { SkeletonCard } from '../components/common/Skeleton';
import { ListView } from '../components/common/ListView';
import { Container, Stack, Grid, Flex } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text, Label } from '../design-system/components/Typography';
import { Badge } from '../design-system/components/Decorative';
import { StatusIndicator } from '../design-system/patterns/StatusIndicator';
import { PageHeader } from '../design-system/patterns/PageHeader';
import SearchBar from '../components/common/SearchBar';
import { GroupedList } from '../components/common/GroupedList';
import { GroupingSelector, GroupingOption } from '../components/common/GroupingSelector';
import { groupByStatus, groupByCategory, groupByDate, useGroupLabels } from '../utils/grouping';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { animations } from '../design-system/tokens/animations';
import { shadows } from '../design-system/tokens/shadows';
import { breakpoints } from '../design-system/tokens/breakpoints';
const ChangeRequestsContainer = styled(Container)`
  max-width: 1200px;
`;

const FiltersSection = styled(Card)`
  padding: ${spacing[8]};
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 400px;
`;

const FiltersGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[5]};
`;
const Select = styled.select`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.xl};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeInOut
  )};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
`;
const ChangeRequestsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 400px), 1fr));
  gap: ${spacing[6]};
  margin-bottom: ${spacing[8]};
`;
const ChangeRequestCard = styled(Card)`
  padding: ${spacing[6]};
  transition: ${animations.transition(
    ['transform', 'box-shadow', 'border-color'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  cursor: pointer;
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => shadows.colored.lg(props.theme.colors.primary)};
    border-color: ${props => props.theme.colors.primary};
  }
`;
const CardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[5]};
  padding-bottom: ${spacing[5]};
  border-bottom: ${borders.width.thin} solid
    ${props => props.theme.colors.border};
`;
const CardTitle = styled.div`
  flex: 1;
  min-width: 0;
`;
const FieldName = styled.h3`
  margin: 0 0 ${spacing[2]} 0;
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.primary};
`;
const EntityTypeLabel = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
`;
const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[5]};
`;
const ValueComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[4]};

  ${breakpoints.down.sm} {
    grid-template-columns: 1fr;
  }
`;
const ValueBox = styled.div`
  padding: ${spacing[3]};
  border-radius: ${borders.radius.lg};
  background: ${props => props.theme.colors.surfaceHover};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
`;
const ValueLabel = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.primary};
  margin-bottom: ${spacing[2]};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
`;
const ValueText = styled.div`
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  word-break: break-word;
`;
const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  padding-top: ${spacing[5]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};
`;
const DateInfo = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;
const RejectionReason = styled.div`
  margin-top: ${spacing[3]};
  padding: ${spacing[3]};
  background: ${props => props.theme.colors.error}15;
  border-left: 3px solid ${props => props.theme.colors.error};
  border-radius: ${borders.radius.sm};
`;
const RejectionLabel = styled.div`
  color: ${props => props.theme.colors.error};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
  margin-bottom: ${spacing[1]};
`;
const RejectionText = styled.div`
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

const FilterGroup = styled(Stack)`
  flex-direction: column;
  gap: ${spacing[2]};
`;

const StatusBadge = styled(Badge)<{ status: ChangeRequestStatus }>`
  ${props => {
    switch (props.status) {
      case ChangeRequestStatus.Approved:
        return `background: ${props.theme.colors.success}; color: ${props.theme.colors.surface};`;
      case ChangeRequestStatus.Rejected:
        return `background: ${props.theme.colors.error}; color: ${props.theme.colors.surface};`;
      case ChangeRequestStatus.Pending:
        return `background: ${props.theme.colors.warning}; color: ${props.theme.colors.text};`;
      default:
        return '';
    }
  }}
`;

const CardBody = styled.div`
  padding-top: ${spacing[4]};
`;

const ChangeDetail = styled.div`
  margin-bottom: ${spacing[3]};
`;

const ChangeLabel = styled(Text)`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${spacing[1]};
`;

const ChangeValue = styled(Text)`
  font-size: ${typography.fontSize.base};
  color: ${props => props.theme.colors.text};
`;

const EntityType = styled(Text)`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const ChangeRequests: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const { error: showError } = useToast();
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ChangeRequestStatus | 'all'>(
    'all'
  );
  const [entityTypeFilter, setEntityTypeFilter] = useState<ChangeRequestEntityType | 'all'>(
    'all'
  );
  const [selectedRequest, setSelectedRequest] = useState<ChangeRequest | null>(
    null
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [grouping, setGrouping] = useState<string>('none');
  const { getStatusLabel, getDateLabel } = useGroupLabels();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadChangeRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [changeRequests, debouncedSearch, statusFilter, entityTypeFilter]);

  const loadChangeRequests = async () => {
    setLoading(true);
    try {
      const requests = await changeRequestService.getMyChangeRequests();
      setChangeRequests(requests);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('changeRequests.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...changeRequests];

    // Search filter
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        cr =>
          cr.fieldName.toLowerCase().includes(searchLower) ||
          (cr.oldValue && cr.oldValue.toLowerCase().includes(searchLower)) ||
          (cr.newValue && cr.newValue.toLowerCase().includes(searchLower))
      );
    }

    // Status filter (API may return string enums)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(cr =>
        enumMatches(ChangeRequestStatus, cr.status, statusFilter)
      );
    }

    // Entity type filter
    if (entityTypeFilter !== 'all') {
      filtered = filtered.filter(cr =>
        enumMatches(ChangeRequestEntityType, cr.entityType, entityTypeFilter)
      );
    }

    setFilteredRequests(filtered);
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatusFilter('all');
    setEntityTypeFilter('all');
  };

  // Grouping options
  const groupingOptions: GroupingOption[] = [
    { value: 'none', label: t('grouping.none') },
    { value: 'status', label: t('grouping.byStatus') },
    { value: 'entityType', label: t('grouping.byEntityType') },
    { value: 'date', label: t('grouping.byDate') },
  ];

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const datePart = formatAthensDate(dateString, i18n.language);
    const timePart = new Intl.DateTimeFormat(
      i18n.language === 'gr' || i18n.language.startsWith('gr') ? 'el-GR' : 'en-GB',
      {
        timeZone: ATHENS_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
      }
    ).format(date);
    return `${datePart}, ${timePart}`;
  };

  const getEntityTypeLabel = (
    type?: ChangeRequestEntityType | string | number | null
  ): string => {
    const normalized = tryNormalizeEnumValue(ChangeRequestEntityType, type);
    if (normalized === ChangeRequestEntityType.IndividualMember) {
      return t('changeRequests.individualMember');
    }
    if (normalized === ChangeRequestEntityType.Institution) {
      return t('changeRequests.institution');
    }
    return t('common.unknown', { defaultValue: 'Unknown' });
  };

  const getRequestStatusLabel = (
    status?: ChangeRequestStatus | string | number | null
  ): string => {
    const normalized = tryNormalizeEnumValue(ChangeRequestStatus, status);
    switch (normalized) {
      case ChangeRequestStatus.Approved:
        return t('changeRequests.statusApproved');
      case ChangeRequestStatus.Rejected:
        return t('changeRequests.statusRejected');
      case ChangeRequestStatus.Pending:
        return t('changeRequests.pending');
      default:
        return t('common.unknown', { defaultValue: 'Unknown' });
    }
  };

  const getRequestStatusTone = (
    status?: ChangeRequestStatus | string | number | null
  ): 'success' | 'error' | 'warning' => {
    const normalized = tryNormalizeEnumValue(ChangeRequestStatus, status);
    if (normalized === ChangeRequestStatus.Approved) return 'success';
    if (normalized === ChangeRequestStatus.Rejected) return 'error';
    return 'warning';
  };

  const handleCardClick = (request: ChangeRequest) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  return (
    <ChangeRequestsContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('changeRequests.title')}
          subtitle={t('changeRequests.showingResults', {
            count: filteredRequests.length,
            total: changeRequests.length,
          }) || `Showing ${filteredRequests.length} of ${changeRequests.length} change requests`}
          pattern={true}
        />

        <FiltersSection variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
          <Stack direction="column" spacing="lg">
            <Heading variant="h3" color="text">
              {t('changeRequests.filters')}
            </Heading>
            <FiltersGrid>
              <FilterGroup>
                <Stack direction="column" spacing="sm">
                  <Label>{t('changeRequests.filterByStatus')}</Label>
                  <Select
                    value={statusFilter === 'all' ? 'all' : statusFilter.toString()}
                    onChange={(e) =>
                      setStatusFilter(e.target.value === 'all' ? 'all' : (parseInt(e.target.value) as ChangeRequestStatus))
                    }
                  >
                    <option value="all">{t('changeRequests.allStatuses')}</option>
                    <option value={ChangeRequestStatus.Pending.toString()}>
                      {t('changeRequests.pending')}
                    </option>
                    <option value={ChangeRequestStatus.Approved.toString()}>
                      {t('changeRequests.statusApproved')}
                    </option>
                    <option value={ChangeRequestStatus.Rejected.toString()}>
                      {t('changeRequests.statusRejected')}
                    </option>
                  </Select>
                </Stack>
              </FilterGroup>
              <FilterGroup>
                <Stack direction="column" spacing="sm">
                  <Label>{t('changeRequests.filterByEntity')}</Label>
                  <Select
                    value={entityTypeFilter === 'all' ? 'all' : entityTypeFilter.toString()}
                    onChange={(e) =>
                      setEntityTypeFilter(e.target.value === 'all' ? 'all' : (parseInt(e.target.value) as ChangeRequestEntityType))
                    }
                  >
                    <option value="all">{t('changeRequests.allEntities')}</option>
                    <option value={ChangeRequestEntityType.IndividualMember.toString()}>
                      {t('changeRequests.individualMember')}
                    </option>
                    <option value={ChangeRequestEntityType.Institution.toString()}>
                      {t('changeRequests.institution')}
                    </option>
                  </Select>
                </Stack>
              </FilterGroup>
            </FiltersGrid>
            <Flex direction="row" justify="space-between" align="center" gap="md">
              <Button variant="outline" onClick={clearFilters}>
                {t('changeRequests.clearFilters')}
              </Button>
              <GroupingSelector
                value={grouping}
                onChange={setGrouping}
                options={groupingOptions}
                label={t('grouping.groupBy')}
              />
            </Flex>
          </Stack>
        </FiltersSection>
        {grouping !== 'none' ? (
          <GroupedList
            items={filteredRequests}
            groupBy={(cr) => {
              if (grouping === 'status') {
                return cr.status.toString();
              }
              if (grouping === 'entityType') {
                return cr.entityType.toString();
              }
              if (grouping === 'date') {
                const date = new Date(cr.requestedAt);
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const thisWeek = new Date(today);
                thisWeek.setDate(today.getDate() - 7);
                const thisMonth = new Date(today);
                thisMonth.setMonth(today.getMonth() - 1);
                
                if (date >= today) return 'today';
                if (date >= thisWeek) return 'thisWeek';
                if (date >= thisMonth) return 'thisMonth';
                return 'older';
              }
              return 'other';
            }}
            groupLabel={(key) => {
              if (grouping === 'status') {
                const statusMap: Record<string, string> = {
                  [ChangeRequestStatus.Pending.toString()]: t('changeRequests.pending'),
                  [ChangeRequestStatus.Approved.toString()]: t('changeRequests.statusApproved'),
                  [ChangeRequestStatus.Rejected.toString()]: t('changeRequests.statusRejected'),
                };
                return statusMap[key] || getStatusLabel(key);
              }
              if (grouping === 'entityType') {
                const entityMap: Record<string, string> = {
                  [ChangeRequestEntityType.IndividualMember.toString()]: t('changeRequests.individualMember'),
                  [ChangeRequestEntityType.Institution.toString()]: t('changeRequests.institution'),
                };
                return entityMap[key] || key;
              }
              if (grouping === 'date') {
                return getDateLabel(key);
              }
              return key;
            }}
            renderItem={(cr) => (
              <ChangeRequestCard key={cr.id} onClick={() => handleCardClick(cr)}>
                <CardHeader>
                  <CardTitle>
                    <FieldName>{formatChangeRequestFieldName(cr.fieldName, t)}</FieldName>
                    <StatusBadge
                      status={
                        tryNormalizeEnumValue(ChangeRequestStatus, cr.status) ??
                        ChangeRequestStatus.Pending
                      }
                    >
                      {getRequestStatusLabel(cr.status)}
                    </StatusBadge>
                  </CardTitle>
                  <EntityType>{getEntityTypeLabel(cr.entityType)}</EntityType>
                </CardHeader>
                <CardBody>
                  <ChangeDetail>
                    <ChangeLabel>{t('changeRequests.oldValue')}:</ChangeLabel>
                    <ChangeValue>
                      {formatChangeRequestFieldValue(cr.fieldName, cr.oldValue, t)}
                    </ChangeValue>
                  </ChangeDetail>
                  <ChangeDetail>
                    <ChangeLabel>{t('changeRequests.newValue')}:</ChangeLabel>
                    <ChangeValue>
                      {formatChangeRequestFieldValue(cr.fieldName, cr.newValue, t)}
                    </ChangeValue>
                  </ChangeDetail>
                  <ChangeDetail>
                    <ChangeLabel>{t('changeRequests.requestedAt')}:</ChangeLabel>
                    <ChangeValue>{formatDate(cr.requestedAt)}</ChangeValue>
                  </ChangeDetail>
                </CardBody>
              </ChangeRequestCard>
            )}
            collapsible
            defaultExpanded={[]}
          />
        ) : (
          <ListView
            items={filteredRequests}
            renderItem={(cr) => (
              <ChangeRequestCard key={cr.id} onClick={() => handleCardClick(cr)}>
                <CardHeader>
                  <CardTitle>
                    <FieldName>{formatChangeRequestFieldName(cr.fieldName, t)}</FieldName>
                    <EntityTypeLabel>{getEntityTypeLabel(cr.entityType)}</EntityTypeLabel>
                  </CardTitle>
                  <StatusIndicator
                    status={getRequestStatusTone(cr.status)}
                    size="sm"
                    label={getRequestStatusLabel(cr.status)}
                  />
                </CardHeader>
                <CardContent>
                  <ValueComparison>
                    <ValueBox>
                      <ValueLabel>{t('changeRequests.oldValue')}</ValueLabel>
                      <ValueText>
                        {formatChangeRequestFieldValue(cr.fieldName, cr.oldValue, t)}
                      </ValueText>
                    </ValueBox>
                    <ValueBox>
                      <ValueLabel>{t('changeRequests.newValue')}</ValueLabel>
                      <ValueText>
                        {formatChangeRequestFieldValue(cr.fieldName, cr.newValue, t)}
                      </ValueText>
                    </ValueBox>
                  </ValueComparison>
                </CardContent>
                <CardFooter>
                  <DateInfo>
                    {t('changeRequests.requestedAt')}: {formatDate(cr.requestedAt)}
                  </DateInfo>
                  {cr.reviewedAt && (
                    <DateInfo>
                      {t('changeRequests.reviewedAt')}: {formatDate(cr.reviewedAt)}
                    </DateInfo>
                  )}
                  {cr.rejectionReason && (
                    <RejectionReason>
                      <RejectionLabel>
                        {t('changeRequests.rejectionReason')}
                      </RejectionLabel>
                      <RejectionText>{cr.rejectionReason}</RejectionText>
                    </RejectionReason>
                  )}
                </CardFooter>
              </ChangeRequestCard>
            )}
            loading={loading}
            emptyMessage={t('changeRequests.noRequests')}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder={
              t('changeRequests.searchPlaceholder')
            }
            showPagination={false}
            viewMode="cards"
          />
        )}
        <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedRequest(null);
        }}
        title={t('changeRequests.requestDetails')}
        footer={
          <Button
            onClick={() => {
              setShowDetailsModal(false);
              setSelectedRequest(null);
            }}
          >
            {t('common.close')}
          </Button>
        }
      >
        {selectedRequest && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
            <div>
              <strong
                style={{
                  color: theme.colors.textSecondary,
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {t('changeRequests.fieldName')}
              </strong>
              <div
                style={{
                  color: theme.colors.text,
                  marginTop: spacing[1],
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {formatChangeRequestFieldName(selectedRequest.fieldName, t)}
              </div>
            </div>
            <div>
              <strong
                style={{
                  color: theme.colors.textSecondary,
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {t('changeRequests.entityType')}
              </strong>
              <div
                style={{
                  color: theme.colors.text,
                  marginTop: spacing[1],
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {getEntityTypeLabel(selectedRequest.entityType)}
              </div>
            </div>
            <div>
              <strong
                style={{
                  color: theme.colors.textSecondary,
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {t('changeRequests.status')}
              </strong>
              <div style={{ marginTop: spacing[1] }}>
                <StatusIndicator
                  status={getRequestStatusTone(selectedRequest.status)}
                  size="sm"
                  label={getRequestStatusLabel(selectedRequest.status)}
                />
              </div>
            </div>
            <div>
              <strong
                style={{
                  color: theme.colors.textSecondary,
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {t('changeRequests.oldValue')}
              </strong>
              <div
                style={{
                  color: theme.colors.text,
                  marginTop: spacing[1],
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {formatChangeRequestFieldValue(
                  selectedRequest.fieldName,
                  selectedRequest.oldValue,
                  t
                )}
              </div>
            </div>
            <div>
              <strong
                style={{
                  color: theme.colors.textSecondary,
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {t('changeRequests.newValue')}
              </strong>
              <div
                style={{
                  color: theme.colors.text,
                  marginTop: spacing[1],
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {formatChangeRequestFieldValue(
                  selectedRequest.fieldName,
                  selectedRequest.newValue,
                  t
                )}
              </div>
            </div>
            <div>
              <strong
                style={{
                  color: theme.colors.textSecondary,
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {t('changeRequests.requestedAt')}
              </strong>
              <div
                style={{
                  color: theme.colors.text,
                  marginTop: spacing[1],
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {formatDate(selectedRequest.requestedAt)}
              </div>
            </div>
            {selectedRequest.reviewedAt && (
              <div>
                <strong
                  style={{
                    color: theme.colors.textSecondary,
                    fontSize: typography.fontSize.xs,
                    fontFamily: typography.fontFamily.primary,
                  }}
                >
                  {t('changeRequests.reviewedAt')}
                </strong>
                <div
                  style={{
                    color: theme.colors.text,
                    marginTop: spacing[1],
                    fontFamily: typography.fontFamily.primary,
                  }}
                >
                  {formatDate(selectedRequest.reviewedAt)}
                </div>
              </div>
            )}
            {selectedRequest.rejectionReason && (
              <div>
                <strong
                  style={{
                    color: theme.colors.textSecondary,
                    fontSize: typography.fontSize.xs,
                    fontFamily: typography.fontFamily.primary,
                  }}
                >
                  {t('changeRequests.rejectionReason')}
                </strong>
                <div
                  style={{
                    color: theme.colors.error,
                    marginTop: spacing[1],
                    padding: spacing[3],
                    background: theme.colors.error + '15',
                    borderRadius: borders.radius.sm,
                    fontFamily: typography.fontFamily.primary,
                  }}
                >
                  {selectedRequest.rejectionReason}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      </Stack>
    </ChangeRequestsContainer>
  );
};
export default ChangeRequests;
